/**
 * One "go back one step" decision for the whole app, shared by every caller
 * that needs it (today: the Android hardware back button).
 *
 * The ordering mirrors what a native app does — dismiss what is layered on top
 * before moving the thing underneath — so a back press never slides panels
 * around behind a full-screen modal the user is still looking at.
 *
 * Kept free of store imports so the policy can be exercised on its own.
 */

import type { PanelBackEntry } from 'state/panelBackStack'

/**
 * 'handled'   — something moved; the caller should swallow the back press.
 * 'exhausted' — nothing left to go back to; the caller should fall through to
 *               its platform default (on Android, background the app).
 */
export type BackNavigationResult = 'handled' | 'exhausted'

export type BackNavigationDeps = {
    /**
     * Closes the topmost modal <dialog> (the HTML ordinal fullscreen viewer) and returns true
     * when there was one. It sits above every other layer and makes them inert, so it goes first.
     */
    closeTopLayerDialog: () => boolean
    /** Closes the topmost maximized image lightbox and returns true when there was one. */
    closeLightbox: () => boolean
    isCryptoModalOpen: () => boolean
    closeCryptoModal: () => void
    isModalOpen: () => boolean
    closeModal: () => void
    isStoryOpen: () => boolean
    closeStoryModal: () => void
    isSidebarOpen: () => boolean
    closeSidebar: () => void
    takePreviousPanel: () => PanelBackEntry | null
    /** Returns false when the panel could not actually be centered. */
    slideToPanel: (panelId: string) => boolean
}

export function performBackNavigation(deps: BackNavigationDeps): BackNavigationResult {
    if (deps.closeTopLayerDialog()) return 'handled'

    if (deps.closeLightbox()) return 'handled'

    if (deps.isCryptoModalOpen()) {
        deps.closeCryptoModal()
        return 'handled'
    }

    if (deps.isModalOpen()) {
        deps.closeModal()
        return 'handled'
    }

    if (deps.isStoryOpen()) {
        deps.closeStoryModal()
        return 'handled'
    }

    if (deps.isSidebarOpen()) {
        deps.closeSidebar()
        return 'handled'
    }

    const previous = deps.takePreviousPanel()
    if (previous && deps.slideToPanel(previous.panelId)) return 'handled'

    return 'exhausted'
}
